import { ContentType } from "@/domain/entities/emails/EmailToSend";
import { verifyEmail, verifyPhone, verifySiret } from "@/lib/utils";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import useSendMail from "./useSendMail";
import useErrorToast from "./utils/useErrorToast";

export interface ContactFormData {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    companyName: string;
    siret: string;
}

export interface FormErrors {
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
    companyName?: string;
    siret?: string;
}

export const useFormValidation = () => {
    const { t } = useTranslation();

    const validateField = (
        field: keyof ContactFormData,
        value: string,
    ): string => {
        switch (field) {
            case "firstName":
                if (!value.trim()) {
                    return t("enterpriseForm.errors.firstNameRequired");
                }
                break;
            case "lastName":
                if (!value.trim()) {
                    return t("enterpriseForm.errors.lastNameRequired");
                }
                break;
            case "phone":
                if (!value.trim()) {
                    return t("enterpriseForm.errors.phoneRequired");
                }
                if (!verifyPhone(value)) {
                    return t("enterpriseForm.errors.phoneInvalid");
                }
                break;
            case "email":
                if (!value.trim()) {
                    return t("enterpriseForm.errors.emailRequired");
                }
                if (!verifyEmail(value)) {
                    return t("enterpriseForm.errors.emailInvalid");
                }
                break;
            case "companyName":
                if (!value.trim()) {
                    return t("enterpriseForm.errors.companyNameRequired");
                }
                break;
            case "siret":
                if (!value.trim()) {
                    return t("enterpriseForm.errors.siretRequired");
                }
                if (!verifySiret(value)) {
                    return t("enterpriseForm.errors.siretInvalid");
                }
                break;
        }

        return "";
    };

    return { validateField };
};

export const useEnterpriseContactForm = () => {
    const showErrorToast = useErrorToast();
    const { t } = useTranslation();
    const { validateField } = useFormValidation();
    const { sendMail } = useSendMail();

    const [formData, setFormData] = useState<ContactFormData>({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        companyName: "",
        siret: "",
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [isSuccess, setIsSuccess] = useState(false);

    const handleInputChange = (field: keyof ContactFormData, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        // Clear error when user starts typing
        if (errors[field as keyof FormErrors]) {
            setErrors((prev) => ({
                ...prev,
                [field]: undefined,
            }));
        }
    };

    const handleBlur = (field: keyof ContactFormData) => {
        const error = validateField(field, formData[field]);
        if (error) {
            setErrors((prev) => ({
                ...prev,
                [field]: error,
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};
        let isValid = true;

        (Object.keys(formData) as (keyof ContactFormData)[]).forEach(
            (field) => {
                const error = validateField(field, formData[field]);
                if (error) {
                    newErrors[field] = error;
                    isValid = false;
                }
            },
        );

        setErrors(newErrors);

        return isValid;
    };

    const sendMailSDK = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const { firstName, lastName, phone, email, companyName, siret } =
                formData;

            const mailContent = `
            <p>Bonjour,</p>
            <p>Vous avez reçu une demande de contact pour l'achat de licences entreprise.</p>
            <p>Voici les informations transmises :</p>
            <p>Prénom: ${firstName}</p>
            <p>Nom: ${lastName}</p>
            <p>Téléphone: ${phone}</p>
            <p>Email: ${email}</p>
            <p>Raison sociale: ${companyName}</p>
            <p>Siret: ${siret}</p>
            <p>Cordialement,</p>
            <p>L'équipe Teamway</p>
            `;

            const emailToSend = {
                id: crypto.randomUUID(),
                subject: "Demande de contact entreprise",
                content: mailContent,
                toRecipients: [
                    import.meta.env.VITE_APP_ENTERPRISE_CONTACT_EMAIL ?? "",
                ],
                ccRecipients: [],
                bccRecipients: [],
                contentType: ContentType.HTML,
                attachments: [],
            };

            await sendMail(emailToSend);

            setIsSuccess(true);
        } catch (error) {
            console.error("Error submitting contact form:", error);
            showErrorToast({
                titleTranslationKey: t("errors.sendMail"),
            });
        }
    };

    const handleSubmit = useMutation({
        mutationFn: sendMailSDK,
    });

    return {
        formData,
        errors,
        handleInputChange,
        handleBlur,
        handleSubmit,
        isSuccess,
    };
};
